import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, ColorType } from 'lightweight-charts';
import screenerApi, { CandleResponse } from '../../services/screenerApi.ts';

// ── Drawing types (same shape stored in DB) ───────────────────────────────────

type DrawingTool =
  | 'CURSOR' | 'TREND_LINE' | 'ARROW' | 'H_LINE' | 'V_LINE'
  | 'H_RAY_RIGHT' | 'H_RAY_LEFT' | 'RECTANGLE' | 'FREEHAND' | 'ARC' | 'TEXT';

interface ChartPoint { time: string; price: number; }

interface Drawing {
  id: string;
  type: DrawingTool;
  points: ChartPoint[];
  color: string;
  lineWidth: number;
  text?: string;
  meta?: Record<string, unknown>;
}

// ── Arc helper ────────────────────────────────────────────────────────────────

function drawEllipseArc(
  ctx: CanvasRenderingContext2D,
  p0: { x: number; y: number },   // left endpoint (pixels)
  p1: { x: number; y: number },   // right endpoint (pixels)
  apex: { x: number; y: number }, // apex point (pixels)
) {
  const adx = p1.x - p0.x;
  const ady = p1.y - p0.y;
  const chordLen = Math.hypot(adx, ady);
  if (chordLen < 1) return;
  const perpX = -ady / chordLen;
  const perpY =  adx / chordLen;
  const midX = (p0.x + p1.x) / 2;
  const midY = (p0.y + p1.y) / 2;
  const h = (apex.x - midX) * perpX + (apex.y - midY) * perpY;
  if (Math.abs(h) < 0.5) return;
  ctx.ellipse(midX, midY, chordLen / 2, Math.abs(h), Math.atan2(ady, adx), Math.PI, 0, h < 0);
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  candles: CandleResponse[] | null;
  stockId: number;
  screenerCode: string;
  height?: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

const MiniChartWithDrawings: React.FC<Props> = ({
  candles,
  stockId,
  screenerCode,
  height = 156,
}) => {
  const containerRef  = useRef<HTMLDivElement>(null);
  const canvasRef     = useRef<HTMLCanvasElement>(null);
  const chartRef      = useRef<any>(null);
  const seriesRef     = useRef<any>(null);
  const chartReadyRef = useRef(false);

  const [drawings, setDrawings] = useState<Drawing[]>([]);
  const drawingsRef = useRef<Drawing[]>(drawings);
  drawingsRef.current = drawings;

  // ── Coordinate helpers ────────────────────────────────────────────────────

  const toPixel = useCallback((time: string, price: number) => {
    if (!chartRef.current || !seriesRef.current) return null;
    const x = chartRef.current.timeScale().timeToCoordinate(time);
    const y = seriesRef.current.priceToCoordinate(price);
    if (x === null || y === null) return null;
    return { x: Math.round(x), y: Math.round(y) };
  }, []);

  // ── Canvas renderer ───────────────────────────────────────────────────────

  const renderDrawings = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !chartRef.current || !seriesRef.current) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const LW = (lw: number) => Math.max(0.5, lw * 0.55);

    const psWidth = (chartRef.current.priceScale('right').width?.() ?? 0);
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, canvas.width - psWidth, canvas.height);
    ctx.clip();

    drawingsRef.current.forEach(d => {
      ctx.strokeStyle = d.color;
      ctx.lineWidth   = LW(d.lineWidth);
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);

      switch (d.type) {
        case 'H_LINE': {
          const y = seriesRef.current.priceToCoordinate(d.points[0].price);
          if (y == null) break;
          ctx.beginPath();
          ctx.moveTo(0, Math.round(y));
          ctx.lineTo(canvas.width, Math.round(y));
          ctx.stroke();
          break;
        }
        case 'H_RAY_RIGHT': {
          const y = seriesRef.current.priceToCoordinate(d.points[0].price);
          const x = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
          if (y == null || x == null) break;
          ctx.beginPath();
          ctx.moveTo(Math.round(x), Math.round(y));
          ctx.lineTo(canvas.width, Math.round(y));
          ctx.stroke();
          break;
        }
        case 'H_RAY_LEFT': {
          const y = seriesRef.current.priceToCoordinate(d.points[0].price);
          const x = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
          if (y == null || x == null) break;
          ctx.beginPath();
          ctx.moveTo(0, Math.round(y));
          ctx.lineTo(Math.round(x), Math.round(y));
          ctx.stroke();
          break;
        }
        case 'V_LINE': {
          const x = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
          if (x == null) break;
          ctx.beginPath();
          ctx.moveTo(Math.round(x), 0);
          ctx.lineTo(Math.round(x), canvas.height);
          ctx.stroke();
          break;
        }
        case 'TEXT': {
          const tx = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
          const ty = seriesRef.current.priceToCoordinate(d.points[0].price);
          if (tx == null || ty == null) break;
          ctx.fillStyle    = d.color;
          ctx.font         = `bold ${Math.max(7, 7 + d.lineWidth)}px sans-serif`;
          ctx.textBaseline = 'middle';
          ctx.fillText(d.text ?? '', Math.round(tx), Math.round(ty));
          break;
        }
        default: {
          const pts = d.points
            .map(p => toPixel(p.time, p.price))
            .filter(Boolean) as { x: number; y: number }[];

          switch (d.type) {
            case 'TREND_LINE': {
              if (pts.length < 2) break;
              ctx.beginPath();
              ctx.moveTo(pts[0].x, pts[0].y);
              ctx.lineTo(pts[1].x, pts[1].y);
              ctx.stroke();
              break;
            }
            case 'ARROW': {
              if (pts.length < 2) break;
              const [p0, p1] = pts;
              const angle   = Math.atan2(p1.y - p0.y, p1.x - p0.x);
              const headLen = Math.max(5, d.lineWidth * 2.5);
              ctx.beginPath();
              ctx.moveTo(p0.x, p0.y);
              ctx.lineTo(p1.x, p1.y);
              ctx.stroke();
              ctx.beginPath();
              ctx.moveTo(p1.x, p1.y);
              ctx.lineTo(p1.x - headLen * Math.cos(angle - Math.PI / 6), p1.y - headLen * Math.sin(angle - Math.PI / 6));
              ctx.lineTo(p1.x - headLen * Math.cos(angle + Math.PI / 6), p1.y - headLen * Math.sin(angle + Math.PI / 6));
              ctx.closePath();
              ctx.fillStyle = d.color;
              ctx.fill();
              break;
            }
            case 'RECTANGLE': {
              if (pts.length < 2) break;
              const [p0, p1] = pts;
              const rx = Math.min(p0.x, p1.x);
              const ry = Math.min(p0.y, p1.y);
              const rw = Math.abs(p1.x - p0.x);
              const rh = Math.abs(p1.y - p0.y);
              ctx.strokeRect(rx, ry, rw, rh);
              ctx.save();
              ctx.globalAlpha = 0.13;
              ctx.fillStyle   = d.color;
              ctx.fillRect(rx, ry, rw, rh);
              ctx.restore();
              break;
            }
            case 'ARC': {
              if (pts.length < 3) break;
              ctx.beginPath();
              drawEllipseArc(ctx, pts[0], pts[1], pts[2]);
              ctx.closePath();
              ctx.save();
              ctx.globalAlpha = 0.13;
              ctx.fillStyle   = d.color;
              ctx.fill();
              ctx.restore();
              ctx.beginPath();
              drawEllipseArc(ctx, pts[0], pts[1], pts[2]);
              ctx.stroke();
              break;
            }
            case 'FREEHAND': {
              if (pts.length < 2) break;
              ctx.beginPath();
              ctx.moveTo(pts[0].x, pts[0].y);
              pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
              ctx.stroke();
              break;
            }
          }
        }
      }
    });

    ctx.restore();
  }, [toPixel]);

  const renderRef = useRef(renderDrawings);
  renderRef.current = renderDrawings;

  // ── Sync canvas size to container (chart uses autoSize) ───────────────────

  const syncCanvas = useCallback(() => {
    const container = containerRef.current;
    if (!container || !canvasRef.current) return;
    const w = Math.round(container.getBoundingClientRect().width) || container.clientWidth;
    canvasRef.current.width  = w;
    canvasRef.current.height = height;
  }, [height]);

  // ── Build chart ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!containerRef.current || !candles?.length) return;
    chartReadyRef.current = false;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#9ca3af',
        fontSize: 9,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      rightPriceScale: { visible: false },
      leftPriceScale:  { visible: false },
      timeScale: { visible: false, borderVisible: false },
      crosshair: {
        vertLine: { visible: false },
        horzLine: { visible: false },
      },
      handleScroll: false,
      handleScale:  false,
    });
    chartRef.current = chart;

    const series = (chart as any).addCandlestickSeries({
      upColor: '#22c55e', downColor: '#ef4444',
      borderVisible: false,
      wickUpColor: '#22c55e', wickDownColor: '#ef4444',
    });
    seriesRef.current = series;
    series.setData(candles.map(c => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close })));

    syncCanvas();
    chart.timeScale().fitContent();

    // Render drawings once chart settles after fitContent
    chart.timeScale().subscribeVisibleTimeRangeChange(() => {
      if (!chartReadyRef.current) {
        chartReadyRef.current = true;
        requestAnimationFrame(() => renderRef.current());
      }
    });

    // Keep canvas in sync as chart auto-resizes
    const ro = new ResizeObserver(() => {
      syncCanvas();
      renderRef.current();
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current  = null;
      seriesRef.current = null;
      chartReadyRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candles, height]);

  // ── Re-render overlay when drawings load ───────────────────────────────────

  useEffect(() => {
    if (chartReadyRef.current) renderDrawings();
  }, [drawings, renderDrawings]);

  // ── Fetch drawings ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!screenerCode || !stockId) return;
    screenerApi.getStockDrawings(screenerCode, stockId)
      .then(res => setDrawings(Array.isArray(res.data.drawings) ? res.data.drawings as Drawing[] : []))
      .catch(() => {});
  }, [screenerCode, stockId]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!candles?.length) {
    return (
      <div style={{
        width: '100%', height,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#6b7280', fontSize: '11px',
        background: 'rgba(128,128,128,0.08)', borderRadius: 4,
      }}>
        No chart data
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height, borderRadius: 4, overflow: 'hidden' }}>
      <div ref={containerRef} style={{ width: '100%', height }} />
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
      />
    </div>
  );
};

export default MiniChartWithDrawings;
